import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { formatResolvedAddress, resolveJobPartyAddress, setJobPartyAddress, type JobPartyRole, type ResolvedPartyAddress } from '../../lib/jobPartyAddress'
import { ADDR_TYPE_META } from '../../lib/customerAddress'

// The address line under a company field on a job: shows WHICH address this party uses on this job, where it came from, and lets staff
// switch to another of the company's addresses ("Change address"). Used by every company field on the Addresses tab.
interface Props {
  jobId: string | undefined
  partyRole: JobPartyRole
  customerId: string          // the company currently selected in the field (may be unsaved)
  userEmail: string | null
  readOnly?: boolean
  // Changes when the job has just been saved, so the address is looked up again against the saved company.
  refreshKey?: string
}

interface AddressChoice {
  id: string
  roles: string[]
  primary_roles: string[]
  address_lines: string[] | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string | null
  label: string | null
}

// Which BoxTop address this job address is linked to (read from our own binding table, no BoxTop call). Always visible; the link is
// made and changed in the Sync Preflight ("Addresses in BoxTop"), because that is where BoxTop's address list is read.
function BoxtopLinkLine({ addressId }: { addressId: string }) {
  const [rows, setRows] = useState<Array<{ boxtop_address_id: number; company_name_snapshot: string; postcode_snapshot: string | null; boxtop_account_number: string; source: string }> | null>(null)
  useEffect(() => {
    let cancelled = false
    supabase.from('customer_boxtop_address_bindings').select('boxtop_address_id, company_name_snapshot, postcode_snapshot, boxtop_account_number, source')
      .eq('customer_address_id', addressId)
      .then(({ data }) => { if (!cancelled) setRows((data ?? []) as never) })
    return () => { cancelled = true }
  }, [addressId])
  if (rows === null) return null
  if (rows.length === 0) return <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">BoxTop address: not linked yet — it is linked (or you are asked to choose) when you Sync.</p>
  return (
    <p className="mt-0.5 text-[11px] text-green-700 dark:text-green-400">
      BoxTop address: {rows.map(r => `${r.company_name_snapshot}${r.postcode_snapshot ? `, ${r.postcode_snapshot}` : ''} (BoxTop address ${r.boxtop_address_id}, account ${r.boxtop_account_number}, ${r.source})`).join('; ')}
      <span className="text-gray-500"> — change it in the Sync Preflight</span>
    </p>
  )
}

export default function JobPartyAddress({ jobId, partyRole, customerId, userEmail, readOnly, refreshKey }: Props) {
  const [resolved, setResolved] = useState<ResolvedPartyAddress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [choices, setChoices] = useState<AddressChoice[] | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!jobId || !customerId) { setResolved(null); return }
    try {
      setError(null)
      setResolved(await resolveJobPartyAddress(jobId, partyRole))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the address')
    }
  }, [jobId, partyRole, customerId, refreshKey])

  useEffect(() => { setChoices(null); load() }, [load])

  if (!customerId) return null
  if (!jobId) return null
  if (error) return <p className="mt-1 text-xs text-red-600">Address could not be loaded: {error}</p>
  if (!resolved) return <p className="mt-1 text-xs text-gray-400">Loading address…</p>

  // The field holds a company the job row does not have yet (picked but not saved): the database cannot know its address yet.
  if (resolved.customer_id && resolved.customer_id !== customerId) {
    return <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">Company changed — save the job, then this company's address can be chosen here.</p>
  }

  const text = formatResolvedAddress(resolved)
  const tone =
    resolved.source === 'chosen' || resolved.source === 'default' ? 'text-gray-500 dark:text-gray-400'
    : resolved.source === 'provisional' ? 'text-amber-700 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400'
  const badge =
    resolved.source === 'chosen' ? { t: 'Address chosen for this job', c: 'bg-green-100 text-green-700' }
    : resolved.source === 'default' ? { t: 'Company default (set when the company was added)', c: 'bg-gray-100 text-gray-600' }
    : resolved.source === 'provisional' ? { t: 'Not chosen on this job yet', c: 'bg-amber-100 text-amber-800' }
    : resolved.source === 'legacy_columns' ? { t: 'From OLD company columns — not in the address book', c: 'bg-red-100 text-red-700' }
    : { t: 'No address', c: 'bg-red-100 text-red-700' }

  const openChoices = async () => {
    const { data, error: qErr } = await supabase
      .from('customer_addresses')
      .select('id, roles, primary_roles, address_lines, city, county, postcode, country, label')
      .eq('customer_id', customerId)
    if (qErr) { setError(qErr.message); return }
    setChoices((data ?? []) as AddressChoice[])
  }

  const choose = async (addressId: string) => {
    if (!jobId) return
    setBusy(true)
    try {
      await setJobPartyAddress(jobId, partyRole, addressId, userEmail)
      setChoices(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the choice')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${badge.c}`}>{badge.t}</span>
        {!readOnly && resolved.customer_id && (
          <button type="button" onClick={() => (choices ? setChoices(null) : openChoices())} className="text-[11px] text-blue-600 hover:underline">
            {choices ? 'Close' : 'Change address'}
          </button>
        )}
        {!readOnly && resolved.source === 'provisional' && resolved.address_id && (
          <button type="button" disabled={busy} onClick={() => choose(resolved.address_id!)} className="text-[11px] text-blue-600 hover:underline disabled:opacity-50">
            Use this address
          </button>
        )}
      </div>
      {text ? <p className={`mt-1 text-xs whitespace-pre-line ${tone}`}>{text}</p> : null}
      {resolved.address_id && partyRole !== 'invoice_to' && partyRole !== 'port_agent' && <BoxtopLinkLine addressId={resolved.address_id} />}
      {resolved.source !== 'chosen' && resolved.source !== 'default' && (
        <p className={`mt-0.5 text-[11px] ${tone}`}>{resolved.reason}</p>
      )}
      {resolved.source === 'none' && (
        <Link to={`/customers/${customerId}`} target="_blank" className="text-[11px] text-blue-600 hover:underline">Add an address on the company page →</Link>
      )}
      {choices && (
        <div className="mt-2 space-y-1 border border-gray-200 dark:border-gray-700 rounded-md p-2">
          {choices.length === 0 ? (
            <p className="text-xs text-gray-500">This company has no addresses. <Link to={`/customers/${customerId}`} target="_blank" className="text-blue-600 hover:underline">Add one on the company page →</Link></p>
          ) : choices.map(c => {
            const current = resolved.address_id === c.id && (resolved.source === 'chosen' || resolved.source === 'default')
            return (
              <div key={c.id} className="flex items-start justify-between gap-2 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap gap-1 mb-0.5">
                    {(c.roles ?? []).map(r => (
                      <span key={r} className={`px-1.5 rounded font-semibold ${ADDR_TYPE_META[r]?.color ?? 'bg-gray-100 text-gray-700'}`}>{ADDR_TYPE_META[r]?.label ?? r}{(c.primary_roles ?? []).includes(r) ? ' ★' : ''}</span>
                    ))}
                    {c.label && <span className="text-gray-500">{c.label}</span>}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">{formatResolvedAddress(c as never) || '—'}</p>
                </div>
                {current ? (
                  <span className="shrink-0 px-2 py-0.5 rounded bg-green-100 text-green-700 font-medium">In use</span>
                ) : (
                  <button type="button" disabled={busy} onClick={() => choose(c.id)}
                    className="shrink-0 px-2 py-0.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50">Use this address</button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
